import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import {
  InteractionRequiredAuthError,
  AccountInfo,
  AuthenticationResult,
} from "@azure/msal-browser";
import { b2cConfig, loginScopes, silentTokenPolicy } from "../config/msalConfig";
import { recoverSignIn } from "../utils/interactiveRedirect";
import { signOut, setTokens, IdTokenClaims } from "../store/slices/authSlice";
import { IRootReducerState } from "../store/IRootReducer";
import { authService } from "../utils/authService";
import { logger } from "../logger-init";
import { activePhoneSession, hasPhoneSession } from "../utils/phoneSession";

/**
 * Custom hook that wraps @azure/msal-react hooks with application-specific logic
 *
 * This hook provides:
 * - Authentication state management
 * - Token acquisition (silent and interactive)
 * - Logout functionality
 * - User information access
 * - Authorization headers generation
 * - B2C-specific error handling (AADB2C90091, AADB2C90118)
 */
export function useMsalAuth() {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const dispatch = useDispatch();

  const accessToken = useSelector((state: IRootReducerState) => state.authReducer.accessToken);
  const idTokenClaims = useSelector((state: IRootReducerState) => state.authReducer.idTokenClaims);

  /**
   * Get the current active account
   */
  const getAccount = useCallback((): AccountInfo | null => {
    return accounts[0] || null;
  }, [accounts]);

  /**
   * Check if user is logged in
   */
  const isLoggedIn = useCallback((): boolean => {
    if (activePhoneSession()) return true;
    return isAuthenticated && accounts.length > 0;
  }, [isAuthenticated, accounts]);

  /**
   * Check if authentication interaction is in progress
   */
  const loginInProgress = useCallback((): boolean => {
    return (
      sessionStorage.getItem(`msal.${b2cConfig.clientId}.interaction.status`) ===
      "interaction_in_progress"
    );
  }, []);

  /**
   * Get access token (silent with fallback to interactive)
   *
   * @param scopes Optional scopes to request (defaults to config scopes)
   * @returns Access token string or null if interaction is in progress
   */
  const getAccessToken = useCallback(
    async (scopes?: string[]): Promise<string | null> => {
      // Signed in by phone: the pass is the token (authService ends an expired one).
      if (hasPhoneSession()) {
        try {
          return await authService.getAccessToken();
        } catch {
          return null;
        }
      }
      try {
        const account = getAccount();
        if (!account) {
          logger.warn("No account found, cannot acquire token");
          return null;
        }

        let tokenResponse: AuthenticationResult;

        try {
          // Try silent token acquisition first (cache / refresh token only — no iframe)
          tokenResponse = await instance.acquireTokenSilent({
            account: account,
            scopes: scopes || b2cConfig.scopes,
            ...silentTokenPolicy,
          });
        } catch (error) {
          logger.error(
            "Silent token acquisition failed",
            error instanceof Error ? error : undefined,
          );

          if (error instanceof InteractionRequiredAuthError) {
            // Check if interaction is already in progress
            if (loginInProgress()) {
              logger.warn(
                "Authentication interaction already in progress, skipping acquireTokenRedirect",
              );
              return null;
            }

            // Sign in again: back to the sign-in page, or the page's single
            // shared B2C redirect when that page is off (see
            // interactiveRedirect.ts); the page is about to be left either way.
            recoverSignIn(instance, {
              account: account,
              scopes: scopes || b2cConfig.scopes,
            }).catch((redirectError: unknown) => {
              logger.error(
                "Interactive token acquisition failed",
                redirectError instanceof Error ? redirectError : undefined,
              );
            });

            // Redirect will happen, return null
            return null;
          }

          throw error;
        }

        // Update Redux store with tokens
        if (tokenResponse) {
          dispatch(
            setTokens({
              accessToken: tokenResponse.accessToken,
              idTokenClaims: tokenResponse.idTokenClaims as IdTokenClaims,
            }),
          );
          return tokenResponse.accessToken;
        }

        return null;
      } catch (error) {
        logger.error("Error acquiring access token", error instanceof Error ? error : undefined);
        return null;
      }
    },
    [instance, getAccount, loginInProgress, dispatch],
  );

  /**
   * Perform login redirect
   *
   * @param scopes Optional scopes to request
   */
  const login = useCallback(
    async (scopes?: string[]): Promise<void> => {
      if (loginInProgress()) {
        logger.warn("Login already in progress");
        return;
      }

      try {
        await instance.loginRedirect({
          scopes: scopes || loginScopes,
        });
      } catch (error) {
        logger.error("Login failed", error instanceof Error ? error : undefined);
        throw error;
      }
    },
    [instance, loginInProgress],
  );

  /**
   * Perform logout
   * Calls backend logout API, clears Redux state, and performs MSAL logout
   */
  const logout = useCallback(async (): Promise<void> => {
    if (hasPhoneSession()) {
      // No Microsoft session to end: forget the phone sign-in and go to the sign-in page.
      dispatch(signOut());
      await authService.logout();
      return;
    }
    try {
      // Call backend logout API using current token from Redux
      if (accessToken) {
        await fetch("/api/user/logout", {
          method: "POST",
          headers: {
            "Accept": "application/json",
            "Content-Type": "application/json",
            "Authorization": `Bearer ${accessToken}`,
          },
        });
      }

      // Clear Redux state
      dispatch(signOut());

      // Clear tenant/instance selection BEFORE redirect (redirect prevents code after it from running)
      authService.clearInstanceSelection();

      // Perform MSAL logout (this redirects, so code after this won't execute)
      await instance.logoutRedirect({
        postLogoutRedirectUri: b2cConfig.postLogoutRedirectUri,
      });
    } catch (error) {
      logger.error("Logout failed", error instanceof Error ? error : undefined);
      throw error;
    }
  }, [instance, accessToken, dispatch]);

  /**
   * Get current user information
   * Returns user data from idTokenClaims in Redux store
   */
  const currentUser = useCallback(() => {
    const phone = activePhoneSession();
    if (phone) {
      return { name: phone.name, emails: phone.email, isAccountEnabled: true, id: phone.userId };
    }
    const account = getAccount();

    if (!account) {
      return null;
    }

    if (idTokenClaims) {
      const claims = idTokenClaims as IdTokenClaims & {
        given_name?: string;
        family_name?: string;
        city?: string;
        country?: string;
        jobtitle?: string;
        extension_IsPlatformAdmin?: boolean;
        account_Enabled?: boolean;
      };
      return {
        name: claims.name,
        firstName: claims.given_name,
        lastName: claims.family_name,
        emails: claims.email,
        city: claims.city,
        country: claims.country,
        jobtitle: claims.jobtitle,
        isPlatformAdmin: claims.extension_IsPlatformAdmin,
        isAccountEnabled: claims.account_Enabled,
        id: claims.sub,
      };
    }

    return {
      name: account.name,
      id: account.localAccountId,
    };
  }, [getAccount, idTokenClaims]);

  /**
   * Get authorization header for API calls
   * @returns Headers object with Authorization and InstanceId
   */
  const getAuthorizationHeader = useCallback((): Headers => {
    const instanceId = new URLSearchParams(window.location.search).get("instanceid");

    return new Headers({
      "Accept": "application/json",
      "Content-Type": "application/json",
      "Authorization": `Bearer ${accessToken || ""}`,
      "InstanceId": instanceId || "",
    });
  }, [accessToken]);

  /**
   * Get header as plain object (for axios and other libraries)
   */
  const getHeader = useCallback(() => {
    const instanceId = new URLSearchParams(window.location.search).get("instanceid");

    return {
      "Accept": "application/json",
      "Content-Type": "application/json",
      "Authorization": `Bearer ${accessToken || ""}`,
      "InstanceId": instanceId || "",
    };
  }, [accessToken]);

  /**
   * Get multipart authorization header (without Content-Type)
   */
  const getMultiPartAuthorizationHeader = useCallback((): Headers => {
    const instanceId = new URLSearchParams(window.location.search).get("instanceid");

    return new Headers({
      Authorization: `Bearer ${accessToken || ""}`,
      InstanceId: instanceId || "",
    });
  }, [accessToken]);

  /**
   * Get instance ID from query string or sessionStorage
   */
  const getInstanceId = useCallback((): string => {
    return authService.getInstanceId();
  }, []);

  /**
   * Get query string with instance ID
   */
  const getQueryString = useCallback((): string => {
    const instanceId = getInstanceId();
    return instanceId ? `?instanceid=${instanceId}` : "";
  }, [getInstanceId]);

  return {
    // MSAL instance and state
    instance,
    accounts,
    isAuthenticated,
    isLoggedIn: isLoggedIn(),

    // Authentication methods
    login,
    logout,
    getAccessToken,

    // User information
    currentUser: currentUser(),
    getAccount,

    // Helper methods
    getAuthorizationHeader,
    getHeader,
    getMultiPartAuthorizationHeader,
    getInstanceId,
    getQueryString,
    loginInProgress: loginInProgress(),
  };
}
